<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { FhText, FhBadge, FhCard, FhSpinner } from '@fareharbor-com/beacon-vue';
import {
  reports,
  coin,
  order,
  help,
  bookmarks,
  bookingsGrid,
  user,
  bank,
  lock,
} from '@fareharbor-com/beacon-vue/icons';
import { QueryParamName } from '@/common/constants/items';
import QuickLinkCard from '@/components/QuickLinkCard.vue';
import BaseFlexbox from '@/components/ui/BaseFlexbox/BaseFlexbox.vue';
import { useUserRoles } from '@/composables/useUserRoles';
import { useUserStore } from '@/stores/user';

const DASHBOARD_BASE_URL = 'https://fareharbor.com';
const API_DOCS_URL = 'https://developer.fareharbor.com/api/external/v1/';

const userStore = useUserStore();

const {
  isFhdnApiAffiliateEnabled,
  isFhdnApiProviderEnabled,
  isFhdnProviderEnabled,
  isSupplier,
  isFhdnAffiliate,
  inFhdn,
  inFhdnApi,
} = useUserRoles(userStore);

onMounted(() => {
  userStore.fetchUserData();
});

const email = computed(() => userStore.user?.user.email || '');
const companyName = computed(() => userStore.user?.company.name || '');
const companyShortname = computed(
  () => userStore.user?.company.shortName || '',
);

const getDashboardUrl = (path = 'settings/info/contact/') =>
  `${DASHBOARD_BASE_URL}/${companyShortname.value}/dashboard/${path}`;

const getPaymentsUrl = () =>
  `${DASHBOARD_BASE_URL}/${companyShortname.value}/dashboard/settings/payments/`;

const getPasswordResetUrl = () => ({ name: 'request-password-reset' });

const getApiDocsUrl = () => API_DOCS_URL;

const getProductsUrl = () => ({
  name: 'item-list',
  query: { [QueryParamName.SEARCH]: companyShortname.value },
});

const getBookmarksUrl = () => ({
  name: 'item-list',
  query: { [QueryParamName.IS_BOOKMARKED]: 'true' },
});

const getFaqUrl = () => ({ name: 'faqhelp' });

// --- Card Visibility Logic ---
const showReportingCards = computed(
  () => isFhdnAffiliate.value && !isSupplier.value,
);
const showUserDetailsCard = computed(() => true);
const showBankAccountCard = computed(
  () => isFhdnAffiliate.value && !isSupplier.value,
);
const showResetPasswordCard = computed(() => !isFhdnApiAffiliateEnabled.value);
const showYourProductsCard = computed(() => isSupplier.value);
const showBookmarksCard = computed(() => !isFhdnApiAffiliateEnabled.value);
const showFAQCard = computed(
  () =>
    (isFhdnAffiliate.value || isFhdnProviderEnabled.value) &&
    !isFhdnApiAffiliateEnabled.value,
);
</script>

<template>
  <BaseFlexbox
    direction="column"
    class="min-h-screen"
    :style="{ backgroundColor: 'var(--fh-color-bg-surface-subtle)' }"
  >
    <header
      class="p-4"
      :style="{ backgroundColor: 'var(--fh-color-bg-primary-default)' }"
    >
      <FhText tag="h1" variant="heading-xl" :color="'inverted'">
        Your Profile
      </FhText>
    </header>

    <div v-if="!userStore.user" class="flex justify-center items-center flex-1">
      <FhSpinner size="xl" />
    </div>
    <main v-else class="flex-1 p-4 max-w-6xl mx-auto w-full">
      <div
        v-if="userStore.user"
        class="p-4 rounded mb-6"
        :style="{
          backgroundColor: 'var(--fh-color-bg-hint-subtle-default)',
          borderLeft: '4px solid var(--fh-color-border-hint-default)',
        }"
      >
        <FhText variant="body-md" :color="'default'">
          <strong>Note:</strong>
          Links on this page will take you to the FareHarbor Dashboard which
          might have a different login from the one you use on the current
          marketplace.
        </FhText>
      </div>

      <FhCard v-if="userStore.user" class="p-6 mb-6">
        <div class="flex justify-between items-center mb-4">
          <FhText tag="h2" variant="heading-lg" :color="'primary-default'">
            Profile Information
          </FhText>
        </div>
        <div class="space-y-4">
          <div class="flex flex-col sm:flex-row sm:items-start">
            <FhText
              variant="body-md"
              :color="'subtle'"
              class="w-32 font-medium"
            >
              Email:
            </FhText>
            <FhText variant="body-md" :color="'default'">{{ email }}</FhText>
          </div>
          <div class="flex flex-col sm:flex-row sm:items-start">
            <FhText
              variant="body-md"
              :color="'subtle'"
              class="w-32 font-medium"
            >
              Company:
            </FhText>
            <FhText variant="body-md" :color="'default'">
              {{ companyName }}
            </FhText>
          </div>
          <div class="flex flex-col sm:flex-row sm:items-start">
            <FhText
              variant="body-md"
              :color="'subtle'"
              class="w-32 font-medium"
            >
              Programs:
            </FhText>
            <div class="flex flex-wrap gap-2">
              <FhBadge v-if="isFhdnProviderEnabled || inFhdn" variant="success">
                FHDN
              </FhBadge>
              <FhBadge
                v-if="isFhdnApiProviderEnabled || inFhdnApi"
                variant="primary"
              >
                FHDN API
              </FhBadge>
            </div>
          </div>
        </div>
      </FhCard>

      <!-- === Account Settings Block === -->
      <div v-if="userStore.user && !isFhdnApiAffiliateEnabled" class="mb-6">
        <FhText
          tag="h2"
          variant="heading-lg"
          :color="'primary-default'"
          class="mb-4"
        >
          Account Settings
        </FhText>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <!-- User Details Card -->
          <QuickLinkCard
            v-if="showUserDetailsCard"
            :icon="user"
            title="User Details"
            description="View or edit your profile information."
            :href="getDashboardUrl()"
          />

          <!-- Bank Account Card -->
          <QuickLinkCard
            v-if="showBankAccountCard"
            :icon="bank"
            title="Bank Account"
            description="Add or edit your payment details."
            :href="getPaymentsUrl()"
          />

          <!-- Reset Password Card (Only if NOT API Affiliate) -->
          <QuickLinkCard
            v-if="showResetPasswordCard"
            :icon="lock"
            title="Reset Password"
            description="Change your marketplace password."
            :to="getPasswordResetUrl()"
          />
        </div>
      </div>

      <!-- === API Affiliate Tools Block === -->
      <div v-if="userStore.user && isFhdnApiAffiliateEnabled" class="mb-6">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <!-- Reset Password Card (API Affiliate specific location) -->
          <QuickLinkCard
            :icon="lock"
            title="Reset Password"
            description="Change your marketplace password."
            :to="getPasswordResetUrl()"
          />

          <!-- FareHarbor Integration Center Card -->
          <QuickLinkCard
            :icon="help"
            title="Integration Center"
            description="Read our API documentation."
            :href="getApiDocsUrl()"
          />

          <!-- Bookmarks Card (API Affiliate specific location) -->
          <QuickLinkCard
            :icon="bookmarks"
            title="Bookmarks"
            description="View your bookmarked items."
            :to="getBookmarksUrl()"
          />
        </div>
      </div>

      <!-- === Sales & Payments Block === -->
      <div
        v-if="
          userStore.user && showReportingCards && !isFhdnApiAffiliateEnabled
        "
        class="mb-6"
      >
        <FhText
          tag="h2"
          variant="heading-lg"
          :color="'primary-default'"
          class="mb-4"
        >
          Sales & Payments
        </FhText>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <!-- Reports Card -->
          <QuickLinkCard
            v-if="showReportingCards"
            :icon="reports"
            title="Reports"
            description="Get to know your sales at any point in time."
            :href="getDashboardUrl('reports/advanced/fhdn-bookings/')"
          />

          <!-- Payouts Card -->
          <QuickLinkCard
            v-if="showReportingCards"
            :icon="coin"
            title="Payouts"
            description="Check when and how much you got paid."
            :href="getDashboardUrl('reports/payouts/')"
          />

          <!-- Tax Invoices Card -->
          <QuickLinkCard
            v-if="showReportingCards"
            :icon="order"
            title="Tax Invoices"
            description="Download the invoices you might need for your accounting."
            :href="getDashboardUrl('reports/fhdn/')"
          />
        </div>
      </div>

      <!-- === Your Network & Tools Block === -->
      <div
        v-if="
          userStore.user &&
          (showYourProductsCard || showBookmarksCard || showFAQCard) &&
          !isFhdnApiAffiliateEnabled
        "
        class="mb-6"
      >
        <FhText
          tag="h2"
          variant="heading-lg"
          :color="'primary-default'"
          class="mb-4"
        >
          Quick Links
        </FhText>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <!-- Your Products Card -->
          <QuickLinkCard
            v-if="showYourProductsCard"
            :icon="bookingsGrid"
            title="Your Products"
            description="Check which products you are supplying on the network."
            :to="getProductsUrl()"
          />

          <!-- Bookmarks Card (Only if NOT API Affiliate) -->
          <QuickLinkCard
            v-if="showBookmarksCard"
            :icon="bookmarks"
            title="Bookmarks"
            description="View your bookmarked items."
            :to="getBookmarksUrl()"
          />

          <!-- FAQ Card -->
          <QuickLinkCard
            v-if="showFAQCard"
            :icon="help"
            title="FAQ"
            description="Learn more about our program."
            :to="getFaqUrl()"
          />
        </div>
      </div>
    </main>
  </BaseFlexbox>
</template>
